import { useWageProStore } from './wagepro_store';
import type { AppRole, SessionUser } from './types';

export interface UseSessionResult {
  session: SessionUser;
  is_admin: boolean;
  set_role: (role: AppRole) => void;
  toggle_role: () => void;
  /**
   * Whether the admin/employee view toggle should be offered at all.
   *
   * `false` whenever the employer surface is out of reach — a real employee
   * session (the toggle is an EMPLOYER's "preview as employee" affordance,
   * and an employee has no admin view to return to) OR the native Capacitor
   * shell, where the employer console is not offered at all. Derived from
   * `is_employee_view_only` (see `./employee_view_only.ts`), never from
   * `session.role` — that is the value the toggle mutates, so it cannot tell
   * a real employee apart from an employer currently previewing.
   */
  can_switch_to_admin: boolean;
  /**
   * `true` when this runtime/session can never reach the employer surface.
   * Exactly `!can_switch_to_admin`, exposed under its own name for the
   * consumers that read it as a statement about the runtime (e.g.
   * `ConnectLandingGate` skipping employer-only first-run screens) rather
   * than as a question about one affordance.
   */
  is_employee_view_only: boolean;
  /**
   * `true` when the signed-in person is an EMPLOYER (an employer-kind boot),
   * regardless of which surface they are currently being shown.
   *
   * Deliberately NOT the same question as `can_switch_to_admin`: inside the
   * native shell an employer is employee-only (no admin surface offered) but
   * is still an employer, and copy/actions that hinge on "who is this
   * person" — e.g. My Pay's "ask your employer for an invite" vs. "link
   * yourself" — must key off THIS, not off whether a view toggle exists.
   * Conflating the two is what made the employer-on-a-phone case read as an
   * employee with no invite.
   */
  is_employer_session: boolean;
}

/** The signed-in user and the admin/employee view they are currently in. */
export function useSession(): UseSessionResult {
  const { session, set_role, is_employee_session, is_employee_view_only } =
    useWageProStore();
  const is_admin = session.role === 'admin';
  return {
    session,
    is_admin,
    set_role,
    toggle_role: () => {
      set_role(is_admin ? 'employee' : 'admin');
    },
    can_switch_to_admin: !is_employee_view_only,
    is_employee_view_only,
    is_employer_session: !is_employee_session,
  };
}
